import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Backdrop,
  CircularProgress,
} from "@mui/material";

/**  
 * Função auxiliar para atualizar de forma imutável um valor aninhado.
 * Ex.: updateNestedValue(obj, ["emit", "enderEmit", "xLgr"], "Nova Rua")
 */
function updateNestedValue(obj, path, newValue) {
  if (path.length === 0) return newValue;
  const key = path[0];
  return {
    ...obj,
    [key]:
      path.length === 1
        ? newValue
        : updateNestedValue(obj[key] || {}, path.slice(1), newValue),
  };
}

function NFEditorComplete() {
  // Definição dos passos do Stepper
  const steps = [
    "Upload XML",
    "Cabeçalho (infNFe)",
    "Itens (det)",
    "Totais/Transp/Cobr/Pag",
    "Protocolo (protNFe)",
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [isParsing, setIsParsing] = useState(false);
  const [rawXml, setRawXml] = useState("");
  const [nfeData, setNfeData] = useState(null);

  // Helpers para atualizar os dados
  const updateHeaderField = (path, value) => {
    setNfeData((prev) => ({
      ...prev,
      header: updateNestedValue(prev.header, path, value),
    }));
  };

  const updateItemField = (index, path, value) => {
    setNfeData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = updateNestedValue(newItems[index], path, value);
      return { ...prev, items: newItems };
    });
  };

  const updateProtocolField = (path, value) => {
    setNfeData((prev) => ({
      ...prev,
      protocol: updateNestedValue(prev.protocol, path, value),
    }));
  };

  // Remove declarações de namespace para facilitar o parsing
  const removeNamespaces = (xmlStr) =>
    xmlStr.replace(/xmlns(:\w+)?="[^"]*"/g, "");

 /* // Função recursiva simples para converter XML para objeto JSON
  function xmlToJson(xml) {
    let obj = {};
    if (xml.nodeType === 1) {
      if (xml.attributes && xml.attributes.length > 0) {
        obj["@attributes"] = {};
        for (let j = 0; j < xml.attributes.length; j++) {
          const attr = xml.attributes.item(j);
          obj["@attributes"][attr.nodeName] = attr.nodeValue;
        }
      }
    } else if (xml.nodeType === 3) {
      obj = xml.nodeValue;
    }
    if (xml.hasChildNodes()) {
      for (let i = 0; i < xml.childNodes.length; i++) {
        const item = xml.childNodes.item(i);
        const nodeName = item.nodeName;
        const value = xmlToJson(item);
        if (value === null || (typeof value === "string" && value.trim() === ""))
          continue;
        if (obj[nodeName] === undefined) {
          obj[nodeName] = value;
        } else {
          if (!Array.isArray(obj[nodeName])) {
            obj[nodeName] = [obj[nodeName]];
          }
          obj[nodeName].push(value);
        }
      }
    }
    return obj;
  }*/

    function xmlToJson(xml) {
        let obj = {};
      
        if (xml.nodeType === 1) { // Elemento
          if (xml.attributes && xml.attributes.length > 0) {
            obj["@attributes"] = {};
            for (let j = 0; j < xml.attributes.length; j++) {
              const attr = xml.attributes.item(j);
              obj["@attributes"][attr.nodeName] = attr.nodeValue;
            }
          }
        } else if (xml.nodeType === 3) { // Texto
          return xml.nodeValue.trim();
        }
      
        if (xml.hasChildNodes()) {
          for (let i = 0; i < xml.childNodes.length; i++) {
            const item = xml.childNodes.item(i);
            const nodeName = item.nodeName;
            const value = xmlToJson(item);
      
            if (value === null || value === "") continue;
      
            if (obj[nodeName] === undefined) {
              obj[nodeName] = value;
            } else {
              if (!Array.isArray(obj[nodeName])) {
                obj[nodeName] = [obj[nodeName]];
              }
              obj[nodeName].push(value);
            }
          }
        }
      
        // Remover "#text" quando for o único campo
        if (Object.keys(obj).length === 1 && obj["#text"] !== undefined) {
          return obj["#text"];
        }
      
        return obj;
      }
      

  // Lê o arquivo XML selecionado pelo usuário
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      setRawXml(text);
      parseXML(text);
    };
    reader.onerror = () => {
      setIsParsing(false);
    };
    reader.readAsText(file);
  };

  // Faz o parsing do XML e extrai os dados da NF‑e
  const parseXML = (xmlStr) => {
    try {
      const cleanXml = removeNamespaces(xmlStr);
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(cleanXml, "text/xml");
      if (xmlDoc.getElementsByTagName("parsererror").length) {
        alert("Erro ao parsear XML.");
        setIsParsing(false);
        return;
      }
      // A estrutura da NF-e: <nfeProc> → <NFe> → <infNFe> (cabeçalho) e depois o protocolo (<protNFe>)
      const nfeProcElem = xmlDoc.getElementsByTagName("nfeProc")[0];
      if (!nfeProcElem) {
        alert("Elemento nfeProc não encontrado.");
        setIsParsing(false);
        return;
      }
      const nfeProcJson = xmlToJson(nfeProcElem);
      // Acesse a NF-e e o cabeçalho (infNFe)
      const nfe = nfeProcJson.NFe;
      if (!nfe || !nfe.infNFe) {
        alert("Elemento infNFe não encontrado.");
        setIsParsing(false);
        return;
      }
      const header = nfe.infNFe;
      // Itens: o elemento <det> pode ser único ou um array
      let items = [];
      if (header.det) {
        items = Array.isArray(header.det) ? header.det : [header.det];
      }
      // Protocolo: normalmente fora de infNFe, em protNFe
      const protocol = nfeProcJson.protNFe || null;
      console.log({header,items,protocol})
      setNfeData({ header, items, protocol });
      setActiveStep(1);
    } catch (error) {
      console.error("Erro ao processar XML:", error);
      alert("Erro ao processar XML.");
    }
    setIsParsing(false);
  };

  // ────────────────────────────── RENDERIZAÇÃO DAS SEÇÕES ──────────────────────────────

  // Seção "ide" – Identificação
  const renderIdeSection = () => {
    const ide = nfeData.header.ide || {};
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Identificação (ide)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <TextField
              label="cUF"
              fullWidth
              value={ide.cUF || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "cUF"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="cNF"
              fullWidth
              value={ide.cNF || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "cNF"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="natOp"
              fullWidth
              value={ide.natOp || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "natOp"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="mod"
              fullWidth
              value={ide.mod || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "mod"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="serie"
              fullWidth
              value={ide.serie || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "serie"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="nNF"
              fullWidth
              value={ide.nNF || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "nNF"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="dhEmi"
              fullWidth
              value={ide.dhEmi || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "dhEmi"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="dhSaiEnt"
              fullWidth
              value={ide.dhSaiEnt || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "dhSaiEnt"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="tpNF"
              fullWidth
              value={ide.tpNF || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "tpNF"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="idDest"
              fullWidth
              value={ide.idDest || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "idDest"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="cMunFG"
              fullWidth
              value={ide.cMunFG || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "cMunFG"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="tpImp"
              fullWidth
              value={ide.tpImp || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "tpImp"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="tpEmis"
              fullWidth
              value={ide.tpEmis || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "tpEmis"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="cDV"
              fullWidth
              value={ide.cDV || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "cDV"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="tpAmb"
              fullWidth
              value={ide.tpAmb || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "tpAmb"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="finNFe"
              fullWidth
              value={ide.finNFe || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "finNFe"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="indFinal"
              fullWidth
              value={ide.indFinal || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "indFinal"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="indPres"
              fullWidth
              value={ide.indPres || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "indPres"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="indIntermed"
              fullWidth
              value={ide.indIntermed || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "indIntermed"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="procEmi"
              fullWidth
              value={ide.procEmi || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "procEmi"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="verProc"
              fullWidth
              value={ide.verProc || ""}
              onChange={(e) =>
                updateHeaderField(["ide", "verProc"], e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Seção "emit" – Emitente e seu endereço
  const renderEmitSection = () => {
    const emit = nfeData.header.emit || {};
    const enderEmit = emit.enderEmit || {};
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Emitente (emit)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="CNPJ"
              fullWidth
              value={emit.CNPJ || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "CNPJ"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label="xNome"
              fullWidth
              value={emit.xNome || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "xNome"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="xFant"
              fullWidth
              value={emit.xFant || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "xFant"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="IE"
              fullWidth
              value={emit.IE || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "IE"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="IM"
              fullWidth
              value={emit.IM || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "IM"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="CNAE"
              fullWidth
              value={emit.CNAE || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "CNAE"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="CRT"
              fullWidth
              value={emit.CRT || ""}
              onChange={(e) =>
                updateHeaderField(["emit", "CRT"], e.target.value)
              }
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Endereço do Emitente (enderEmit)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="xLgr"
                fullWidth
                value={enderEmit.xLgr || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "xLgr"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="nro"
                fullWidth
                value={enderEmit.nro || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "nro"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="xBairro"
                fullWidth
                value={enderEmit.xBairro || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "xBairro"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="cMun"
                fullWidth
                value={enderEmit.cMun || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "cMun"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="xMun"
                fullWidth
                value={enderEmit.xMun || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "xMun"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="UF"
                fullWidth
                value={enderEmit.UF || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "UF"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="CEP"
                fullWidth
                value={enderEmit.CEP || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "CEP"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="cPais"
                fullWidth
                value={enderEmit.cPais || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "cPais"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="xPais"
                fullWidth
                value={enderEmit.xPais || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "xPais"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="fone"
                fullWidth
                value={enderEmit.fone || ""}
                onChange={(e) =>
                  updateHeaderField(["emit", "enderEmit", "fone"], e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  // Seção "dest" – Destinatário e seu endereço
  const renderDestSection = () => {
    const dest = nfeData.header.dest || {};
    const enderDest = dest.enderDest || {};
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Destinatário (dest)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="CNPJ"
              fullWidth
              value={dest.CNPJ || ""}
              onChange={(e) =>
                updateHeaderField(["dest", "CNPJ"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label="xNome"
              fullWidth
              value={dest.xNome || ""}
              onChange={(e) =>
                updateHeaderField(["dest", "xNome"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="indIEDest"
              fullWidth
              value={dest.indIEDest || ""}
              onChange={(e) =>
                updateHeaderField(["dest", "indIEDest"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label="IE"
              fullWidth
              value={dest.IE || ""}
              onChange={(e) =>
                updateHeaderField(["dest", "IE"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label="email"
              fullWidth
              value={dest.email || ""}
              onChange={(e) =>
                updateHeaderField(["dest", "email"], e.target.value)
              }
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Endereço do Destinatário (enderDest)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="xLgr"
                fullWidth
                value={enderDest.xLgr || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "xLgr"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="nro"
                fullWidth
                value={enderDest.nro || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "nro"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="xBairro"
                fullWidth
                value={enderDest.xBairro || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "xBairro"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="cMun"
                fullWidth
                value={enderDest.cMun || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "cMun"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="xMun"
                fullWidth
                value={enderDest.xMun || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "xMun"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="UF"
                fullWidth
                value={enderDest.UF || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "UF"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="CEP"
                fullWidth
                value={enderDest.CEP || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "CEP"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="cPais"
                fullWidth
                value={enderDest.cPais || ""}
                onChange={(e) =>
                  updateHeaderField(["dest", "enderDest", "cPais"], e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  // Seção "infAdic" – Informações Adicionais
  const renderInfAdicSection = () => {
    const infAdic = nfeData.header.infAdic || {};
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Informações Adicionais (infAdic)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="infAdFisco"
              fullWidth
              value={infAdic.infAdFisco || ""}
              onChange={(e) =>
                updateHeaderField(["infAdic", "infAdFisco"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="infCpl"
              fullWidth
              value={infAdic.infCpl || ""}
              onChange={(e) =>
                updateHeaderField(["infAdic", "infCpl"], e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Seção "infRespTec" – Informações do Responsável Técnico
  const renderInfRespTecSection = () => {
    const infRespTec = nfeData.header.infRespTec || {};
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Informações do Responsável Técnico (infRespTec)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <TextField
              label="CNPJ"
              fullWidth
              value={infRespTec.CNPJ || ""}
              onChange={(e) =>
                updateHeaderField(["infRespTec", "CNPJ"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="xContato"
              fullWidth
              value={infRespTec.xContato || ""}
              onChange={(e) =>
                updateHeaderField(["infRespTec", "xContato"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="email"
              fullWidth
              value={infRespTec.email || ""}
              onChange={(e) =>
                updateHeaderField(["infRespTec", "email"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="fone"
              fullWidth
              value={infRespTec.fone || ""}
              onChange={(e) =>
                updateHeaderField(["infRespTec", "fone"], e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Renderiza todas as seções do cabeçalho (infNFe)
  const renderHeaderStep = () => {
    return (
      <Box>
        {renderIdeSection()}
        {renderEmitSection()}
        {renderDestSection()}
        {renderInfAdicSection()}
        {renderInfRespTecSection()}
      </Box>
    );
  };

  // Seção "Itens" – para cada <det>
  const renderItensStep = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Itens (det)
        </Typography>
        {nfeData.items.map((item, idx) => {
          const prod = item.prod || {};
          const imposto = item.imposto || {};
          // Para ICMS, PIS e COFINS, considerando o exemplo:
          const icms = imposto.ICMS ? imposto.ICMS.ICMSSN102 || {} : {};
          const pis = imposto.PIS ? imposto.PIS.PISNT || {} : {};
          const cofins = imposto.COFINS ? imposto.COFINS.COFINSNT || {} : {};
          return (
            <Box
              key={idx}
              sx={{
                border: "1px solid #ccc",
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Item {item["@attributes"]?.nItem || idx + 1}
              </Typography>
              <Grid container spacing={2}>
                {/* Campos de PRODUTO */}
                <Grid item xs={2}>
                  <TextField
                    label="cProd"
                    fullWidth
                    value={prod.cProd || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "cProd"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="cEAN"
                    fullWidth
                    value={prod.cEAN || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "cEAN"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="xProd"
                    fullWidth
                    value={prod.xProd || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "xProd"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="NCM"
                    fullWidth
                    value={prod.NCM || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "NCM"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="CFOP"
                    fullWidth
                    value={prod.CFOP || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "CFOP"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="uCom"
                    fullWidth
                    value={prod.uCom || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "uCom"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="qCom"
                    fullWidth
                    value={prod.qCom || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "qCom"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="vUnCom"
                    fullWidth
                    value={prod.vUnCom || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "vUnCom"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="vProd"
                    fullWidth
                    value={prod.vProd || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "vProd"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="cEANTrib"
                    fullWidth
                    value={prod.cEANTrib || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "cEANTrib"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="uTrib"
                    fullWidth
                    value={prod.uTrib || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "uTrib"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="qTrib"
                    fullWidth
                    value={prod.qTrib || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "qTrib"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="vUnTrib"
                    fullWidth
                    value={prod.vUnTrib || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "vUnTrib"], e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="indTot"
                    fullWidth
                    value={prod.indTot || ""}
                    onChange={(e) =>
                      updateItemField(idx, ["prod", "indTot"], e.target.value)
                    }
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Imposto – ICMS
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      label="orig"
                      fullWidth
                      value={icms.orig || ""}
                      onChange={(e) =>
                        updateItemField(idx, ["imposto", "ICMS", "ICMSSN102", "orig"], e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="CSOSN"
                      fullWidth
                      value={icms.CSOSN || ""}
                      onChange={(e) =>
                        updateItemField(idx, ["imposto", "ICMS", "ICMSSN102", "CSOSN"], e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Imposto – PIS
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      label="CST"
                      fullWidth
                      value={pis.CST || ""}
                      onChange={(e) =>
                        updateItemField(idx, ["imposto", "PIS", "PISNT", "CST"], e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Imposto – COFINS
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      label="CST"
                      fullWidth
                      value={cofins.CST || ""}
                      onChange={(e) =>
                        updateItemField(idx, ["imposto", "COFINS", "COFINSNT", "CST"], e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Seção "Totais, Transporte, Cobrança e Pagamento"
  const renderTotaisStep = () => {
    const total = nfeData.header.total
      ? nfeData.header.total.ICMSTot || {}
      : {};
    const transp = nfeData.header.transp || {};
    const cobr = nfeData.header.cobr || {};
    const fat = cobr.fat || {};
    const dup = cobr.dup || {};
    const pag = nfeData.header.pag && nfeData.header.pag.detPag ? nfeData.header.pag.detPag : {};
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Totais (total/ICMSTot)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <TextField
                label="vBC"
                fullWidth
                value={total.vBC || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vBC"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vICMS"
                fullWidth
                value={total.vICMS || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vICMS"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vICMSDeson"
                fullWidth
                value={total.vICMSDeson || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vICMSDeson"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vFCP"
                fullWidth
                value={total.vFCP || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vFCP"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vBCST"
                fullWidth
                value={total.vBCST || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vBCST"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vST"
                fullWidth
                value={total.vST || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vST"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vFCPST"
                fullWidth
                value={total.vFCPST || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vFCPST"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vFCPSTRet"
                fullWidth
                value={total.vFCPSTRet || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vFCPSTRet"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vProd"
                fullWidth
                value={total.vProd || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vProd"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vFrete"
                fullWidth
                value={total.vFrete || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vFrete"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vSeg"
                fullWidth
                value={total.vSeg || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vSeg"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vDesc"
                fullWidth
                value={total.vDesc || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vDesc"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vII"
                fullWidth
                value={total.vII || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vII"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vIPI"
                fullWidth
                value={total.vIPI || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vIPI"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vIPIDevol"
                fullWidth
                value={total.vIPIDevol || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vIPIDevol"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vPIS"
                fullWidth
                value={total.vPIS || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vPIS"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vCOFINS"
                fullWidth
                value={total.vCOFINS || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vCOFINS"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vOutro"
                fullWidth
                value={total.vOutro || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vOutro"], e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="vNF"
                fullWidth
                value={total.vNF || ""}
                onChange={(e) =>
                  updateHeaderField(["total", "ICMSTot", "vNF"], e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Transporte (transp)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                label="modFrete"
                fullWidth
                value={nfeData.header.transp?.modFrete || ""}
                onChange={(e) =>
                  updateHeaderField(["transp", "modFrete"], e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography variant="h6" gutterBottom>
            Cobrança (cobr) e Pagamento (pag)
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Fatura (fat)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  label="nFat"
                  fullWidth
                  value={nfeData.header.cobr?.fat?.nFat || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "fat", "nFat"], e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="vOrig"
                  fullWidth
                  value={nfeData.header.cobr?.fat?.vOrig || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "fat", "vOrig"], e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="vDesc"
                  fullWidth
                  value={nfeData.header.cobr?.fat?.vDesc || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "fat", "vDesc"], e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="vLiq"
                  fullWidth
                  value={nfeData.header.cobr?.fat?.vLiq || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "fat", "vLiq"], e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Duplicata (dup)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  label="nDup"
                  fullWidth
                  value={nfeData.header.cobr?.dup?.nDup || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "dup", "nDup"], e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="dVenc"
                  fullWidth
                  value={nfeData.header.cobr?.dup?.dVenc || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "dup", "dVenc"], e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="vDup"
                  fullWidth
                  value={nfeData.header.cobr?.dup?.vDup || ""}
                  onChange={(e) =>
                    updateHeaderField(["cobr", "dup", "vDup"], e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Typography variant="subtitle1">Pagamento (pag)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  label="tPag"
                  fullWidth
                  value={nfeData.header.pag?.detPag?.tPag || ""}
                  onChange={(e) =>
                    updateHeaderField(["pag", "detPag", "tPag"], e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="vPag"
                  fullWidth
                  value={nfeData.header.pag?.detPag?.vPag || ""}
                  onChange={(e) =>
                    updateHeaderField(["pag", "detPag", "vPag"], e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  };

  // Seção "Protocolo" – protNFe → infProt
  const renderProtocolStep = () => {
    const protocol = nfeData.protocol ? nfeData.protocol.infProt || {} : {};
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Protocolo (protNFe - infProt)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <TextField
              label="tpAmb"
              fullWidth
              value={protocol.tpAmb || ""}
              onChange={(e) =>
                updateProtocolField(["tpAmb"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="verAplic"
              fullWidth
              value={protocol.verAplic || ""}
              onChange={(e) =>
                updateProtocolField(["verAplic"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="chNFe"
              fullWidth
              value={protocol.chNFe || ""}
              onChange={(e) =>
                updateProtocolField(["chNFe"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="dhRecbto"
              fullWidth
              value={protocol.dhRecbto || ""}
              onChange={(e) =>
                updateProtocolField(["dhRecbto"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="nProt"
              fullWidth
              value={protocol.nProt || ""}
              onChange={(e) =>
                updateProtocolField(["nProt"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="digVal"
              fullWidth
              value={protocol.digVal || ""}
              onChange={(e) =>
                updateProtocolField(["digVal"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="cStat"
              fullWidth
              value={protocol.cStat || ""}
              onChange={(e) =>
                updateProtocolField(["cStat"], e.target.value)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="xMotivo"
              fullWidth
              value={protocol.xMotivo || ""}
              onChange={(e) =>
                updateProtocolField(["xMotivo"], e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ────────────────────────────── RENDERIZAÇÃO DO STEPPER ──────────────────────────────

  const renderStepContent = () => {
    if (!nfeData && activeStep !== 0)
      return <Typography>Nenhum dado carregado.</Typography>;
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Carregar arquivo XML da NF-e
            </Typography>
            <Button variant="contained" component="label">
              Selecionar Arquivo
              <input
                type="file"
                accept=".xml"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            {rawXml && (
              <Box mt={2}>
                <Typography variant="subtitle1">
                  Conteúdo do XML:
                </Typography>
                <TextField
                  value={rawXml}
                  multiline
                  minRows={6}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Box>
            )}
          </Box>
        );
      case 1:
        return renderHeaderStep();
      case 2:
        return renderItensStep();
      case 3:
        return renderTotaisStep();
      case 4:
        return renderProtocolStep();
      default:
        return <Typography variant="h6">Finalizado</Typography>;
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Editor Completo de NF-e – Campo a Campo
        </Typography>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, idx) => (
            <Step key={idx}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ mt: 4 }}>{renderStepContent()}</Box>
        <Box
          sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
        >
          <Button onClick={handleBack} disabled={activeStep === 0}>
            Voltar
          </Button>
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && !nfeData}
            >
              Próximo
            </Button>
          )}
        </Box>
      </Paper>
      <Backdrop
        open={isParsing}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default NFEditorComplete;
